import moment from 'jalali-moment'
import { motion, useReducedMotion } from 'framer-motion'
import { FiGift, FiStar, FiTool, FiTrendingUp } from 'react-icons/fi'
import CustomBottomSheet from 'components/bottomSheet'
import type { ChangeKind, Release } from './releases'

export type WhatsNewSource = 'update' | 'sidebar'
export type CloseReason = 'button' | 'dismissed'

const faDigits = (value: string) =>
    value.replace(/\d/g, (d) => '۰۱۲۳۴۵۶۷۸۹'[Number(d)])

const jalaliDate = (date: string) =>
    faDigits(
        moment.from(date, 'en', 'YYYY-MM-DD').locale('fa').format('D MMMM YYYY')
    )

const KINDS: Record<
    ChangeKind,
    { label: string; Icon: typeof FiStar; tile: string; tone: string }
> = {
    new: {
        label: 'قابلیت جدید',
        Icon: FiStar,
        tile: 'bg-brand-soft text-brand-deep',
        tone: 'text-brand-strong',
    },
    better: {
        label: 'بهبود',
        Icon: FiTrendingUp,
        tile: 'bg-blue-6 text-blue-3',
        tone: 'text-blue-5',
    },
    fix: {
        label: 'رفع مشکل',
        Icon: FiTool,
        tile: 'bg-warning-soft text-warning-on',
        tone: 'text-warning-on',
    },
}

// A few glints around the gift. Decoration only, so they sit still for anyone
// who has asked the OS for less motion.
const GLINTS = [
    { className: 'right-[22%] top-5 text-lg text-yellow-1', delay: 0.2 },
    { className: 'left-[24%] top-8 text-sm text-brand', delay: 0.6 },
    { className: 'right-[30%] top-16 text-xs text-brand-strong', delay: 1 },
    {
        className: 'left-[18%] top-[4.5rem] text-base text-yellow-1',
        delay: 1.3,
    },
]

const list = {
    hidden: {},
    show: { transition: { staggerChildren: 0.06, delayChildren: 0.2 } },
}
const row = {
    hidden: { opacity: 0, y: 10 },
    show: { opacity: 1, y: 0, transition: { duration: 0.3 } },
}

const VersionLabel = ({ release }: { release: Release }) => (
    <>
        نسخهٔ{' '}
        <bdi dir="ltr" className="inline-block">
            {faDigits(release.version)}
        </bdi>
        <span className="mx-1.5 text-text-muted">·</span>
        {jalaliDate(release.date)}
    </>
)

interface WhatsNewSheetProps {
    isOpen: boolean
    releases: Release[]
    source: WhatsNewSource
    onClose: (via: CloseReason) => void
}

export default function WhatsNewSheet({
    isOpen,
    releases,
    source,
    onClose,
}: WhatsNewSheetProps) {
    const reduceMotion = useReducedMotion()
    const [latest] = releases

    return (
        <CustomBottomSheet
            isOpen={isOpen && !!latest}
            onDismiss={() => onClose('dismissed')}
            dragHandleOnly
        >
            {latest && (
                <div className="flex max-h-[82vh] flex-col" dir="rtl">
                    <div className="relative shrink-0 overflow-hidden rounded-sheet bg-gradient-to-b from-brand-soft to-white px-5 pb-4 pt-6 text-center">
                        {GLINTS.map((glint) => (
                            <motion.span
                                key={glint.className}
                                aria-hidden
                                className={`pointer-events-none absolute ${glint.className}`}
                                initial={{ opacity: 0, scale: 0.4 }}
                                animate={
                                    reduceMotion
                                        ? { opacity: 0.8, scale: 1 }
                                        : {
                                              opacity: [0, 1, 0.35],
                                              scale: [0.4, 1.1, 0.8],
                                          }
                                }
                                transition={{
                                    duration: 1.8,
                                    delay: glint.delay,
                                    repeat: reduceMotion ? 0 : Infinity,
                                    repeatType: 'reverse',
                                }}
                            >
                                ✦
                            </motion.span>
                        ))}

                        <motion.div
                            className="mx-auto flex h-16 w-16 items-center justify-center rounded-2xl bg-brand text-white shadow-fab"
                            initial={{ scale: 0.5, rotate: -14, opacity: 0 }}
                            animate={{ scale: 1, rotate: 0, opacity: 1 }}
                            transition={{
                                type: 'spring',
                                stiffness: 260,
                                damping: 14,
                                delay: 0.1,
                            }}
                        >
                            <FiGift size={30} />
                        </motion.div>

                        <p className="mb-0 mt-4 text-lg font-bold text-heading">
                            {source === 'update'
                                ? 'همیان تازه شد!'
                                : 'تازه‌های همیان'}
                        </p>
                        <p className="mx-auto mb-0 mt-1 max-w-[18rem] text-sm leading-7 text-text-body">
                            {latest.title}
                        </p>
                        <span className="mt-3 inline-block rounded-full bg-white px-3 py-1 text-xs text-brand-deep shadow-badge">
                            <VersionLabel release={latest} />
                        </span>
                    </div>

                    <div className="-mx-4 mt-1 min-h-0 flex-1 overflow-y-auto overscroll-contain px-4">
                        {releases.map((release, index) => (
                            <section key={release.version}>
                                {index > 0 && (
                                    <div className="mt-4 rounded-card bg-sunken px-3 py-2">
                                        <p className="m-0 text-xs text-text-secondary">
                                            <VersionLabel release={release} />
                                        </p>
                                        <p className="m-0 mt-0.5 text-sm font-bold text-heading">
                                            {release.title}
                                        </p>
                                    </div>
                                )}
                                <motion.ul
                                    className="m-0 list-none p-0"
                                    variants={list}
                                    initial="hidden"
                                    animate="show"
                                >
                                    {release.changes.map((change) => {
                                        const kind = KINDS[change.kind]
                                        return (
                                            <motion.li
                                                key={change.text}
                                                variants={row}
                                                className="flex items-start gap-3 border-b border-divider py-3 last:border-b-0"
                                            >
                                                <span
                                                    className={`mt-0.5 flex h-9 w-9 shrink-0 items-center justify-center rounded-card ${kind.tile}`}
                                                >
                                                    <kind.Icon size={17} />
                                                </span>
                                                <div className="min-w-0 flex-1">
                                                    <p
                                                        className={`m-0 text-[11px] font-bold ${kind.tone}`}
                                                    >
                                                        {kind.label}
                                                    </p>
                                                    <p className="m-0 mt-0.5 text-sm leading-7 text-text-body">
                                                        {change.text}
                                                    </p>
                                                </div>
                                            </motion.li>
                                        )
                                    })}
                                </motion.ul>
                            </section>
                        ))}
                    </div>

                    <button
                        type="button"
                        onClick={() => onClose('button')}
                        className="mt-3 w-full shrink-0 rounded-card border border-green-1 bg-green-1 px-4 py-3 text-center font-bold text-white-test shadow-fab transition-colors hover:border-brand-strong hover:bg-brand-strong active:scale-[0.98]"
                    >
                        متوجه شدم
                    </button>
                </div>
            )}
        </CustomBottomSheet>
    )
}
